const MONTHS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

export const ACTIVE_STUDENT_SCHOOLS = [
  { value: 'ALL', label: 'All Schools' },
  { value: 'MMA', label: 'Mountainside' },
  { value: 'LSMA', label: 'Lakeside' },
  { value: 'TMA', label: 'Treeside' },
  { value: 'MORMA', label: 'Morningside' },
  { value: 'PSMA', label: 'Parkside' },
];

const SCHOOL_ALIASES = {
  mma: 'MMA', mountainside: 'MMA',
  lsma: 'LSMA', lakeside: 'LSMA',
  tma: 'TMA', tsma: 'TMA', treeside: 'TMA',
  mor: 'MORMA', morma: 'MORMA', morningside: 'MORMA',
  psma: 'PSMA', parkside: 'PSMA',
};

const normalize = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
const normalizeId = (value) => String(value || '').trim();
const normalizeSchool = (value) => SCHOOL_ALIASES[normalize(value)] || '';

const inferTimesheetMonth = (timesheet) => {
  const rawMonthYear = String(timesheet?.monthYear || '').trim();
  if (rawMonthYear) {
    const namedMatch = rawMonthYear.match(/^([a-z]+)[\s/-]+(\d{4})$/i);
    if (namedMatch) {
      const monthToken = namedMatch[1].toLowerCase();
      const monthIndex = MONTHS.findIndex((month) => (
        month === monthToken || month.slice(0, 3) === monthToken.slice(0, 3)
      ));
      const year = Number(namedMatch[2]);
      if (monthIndex >= 0 && Number.isInteger(year)) return new Date(year, monthIndex, 1);
    }

    const numericMatch = rawMonthYear.match(/^(\d{1,2})[/-](\d{4})$/);
    if (numericMatch) {
      const monthNumber = Number(numericMatch[1]);
      const year = Number(numericMatch[2]);
      if (monthNumber >= 1 && monthNumber <= 12 && Number.isInteger(year)) {
        return new Date(year, monthNumber - 1, 1);
      }
    }
  }

  const year = Number(timesheet?.year);
  const rawMonthNumber = Number(timesheet?.monthNumber ?? timesheet?.month);
  if (!Number.isInteger(year) || !Number.isInteger(rawMonthNumber)) return null;
  const monthIndex = rawMonthNumber >= 0 && rawMonthNumber <= 11
    ? rawMonthNumber
    : rawMonthNumber >= 1 && rawMonthNumber <= 12
      ? rawMonthNumber - 1
      : null;
  return monthIndex === null ? null : new Date(year, monthIndex, 1);
};

const buildTeacherSchoolLookup = (teachers = []) => {
  const lookup = new Map();
  (Array.isArray(teachers) ? teachers : []).forEach((teacher) => {
    const school = normalizeSchool(teacher?.School || teacher?.school);
    if (!school) return;
    [teacher?.TeacherID, teacher?.teacherId, teacher?.Email, teacher?.email, teacher?.Name, teacher?.teacherName]
      .map(normalize)
      .filter(Boolean)
      .forEach((key) => lookup.set(key, school));
  });
  return lookup;
};

const resolveTimesheetSchool = (timesheet, teacherSchoolLookup) => {
  const direct = normalizeSchool(timesheet?.school || timesheet?.School);
  if (direct) return direct;
  const teacherKeys = [
    timesheet?.teacherId, timesheet?.TeacherID, timesheet?.email, timesheet?.teacherName,
  ].map(normalize).filter(Boolean);
  return teacherKeys.map((key) => teacherSchoolLookup.get(key)).find(Boolean) || '';
};

const matchStudent = (lesson, familyStudents) => {
  const lessonStudentId = normalizeId(lesson?.studentId);
  const lessonStudentName = normalize(lesson?.studentName);
  if (lessonStudentId) {
    const idMatch = familyStudents.find((student) => normalizeId(student?.studentId) === lessonStudentId);
    if (idMatch) return idMatch;
  }
  if (lessonStudentName) {
    const nameMatch = familyStudents.find((student) => normalize(student?.studentName) === lessonStudentName);
    if (nameMatch) return nameMatch;
  }
  return familyStudents.length === 1 ? familyStudents[0] : null;
};

const getStudentIdentity = (lesson, family) => {
  const familyStudents = Array.isArray(family?.listOfFamilyStudents)
    ? family.listOfFamilyStudents : [];
  const matchedStudent = matchStudent(lesson, familyStudents);
  const familyKey = normalizeId(family?.id || family?.familyId || family?.familyWaveId)
    || normalize(family?.email)
    || normalize(family?.familyName);
  const studentKey = normalizeId(matchedStudent?.studentId || lesson?.studentId)
    || normalize(matchedStudent?.studentName || lesson?.studentName);
  return familyKey && studentKey ? `${familyKey}|${studentKey}` : '';
};

export const buildActiveStudentsBySchool = ({ timesheets = [], teachers = [] } = {}) => {
  const teacherSchoolLookup = buildTeacherSchoolLookup(teachers);
  const monthGroups = new Map();

  (Array.isArray(timesheets) ? timesheets : []).forEach((timesheet) => {
    if (!timesheet || typeof timesheet !== 'object') return;
    const date = inferTimesheetMonth(timesheet);
    if (!date) return;
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!monthGroups.has(monthKey)) {
      monthGroups.set(monthKey, { date, all: new Set(), schools: new Map() });
    }
    const group = monthGroups.get(monthKey);
    const timesheetSchool = resolveTimesheetSchool(timesheet, teacherSchoolLookup);

    (Array.isArray(timesheet?.families) ? timesheet.families : []).forEach((family) => {
      const school = normalizeSchool(family?.school) || timesheetSchool;
      (Array.isArray(family?.lessons) ? family.lessons : []).forEach((lesson) => {
        const identity = getStudentIdentity(lesson, family);
        if (!identity) return;
        group.all.add(identity);
        if (school) {
          if (!group.schools.has(school)) group.schools.set(school, new Set());
          group.schools.get(school).add(identity);
        }
      });
    });
  });

  const result = Object.fromEntries(ACTIVE_STUDENT_SCHOOLS.map(({value}) => [value, []]));
  Array.from(monthGroups.values())
    .sort((left, right) => left.date.getTime() - right.date.getTime())
    .forEach((group) => {
      const month = group.date.toLocaleDateString('en-US', {month: 'short', year: 'numeric'});
      result.ALL.push({month, activeStudents: group.all.size});
      ACTIVE_STUDENT_SCHOOLS.filter(({value}) => value !== 'ALL').forEach(({value}) => {
        const studentSet = group.schools.get(value);
        if (studentSet) result[value].push({month, activeStudents: studentSet.size});
      });
    });

  return result;
};
